import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Seo from "@/components/seo/Seo";
import Reveal from "@/components/ui-primitives/Reveal";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import { executive, CANONICAL_ORIGIN } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  company: "",
  email: "",
  role: "",
  inquiryType: "General Inquiry",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please provide your name, email, and a brief message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSubmitted(true);
      toast.success("Thank you — a member of the office will be in touch.");
      setForm(initial);
    } catch (err) {
      toast.error("Unable to send. Please try again or email the office directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    url: `${CANONICAL_ORIGIN}/leadership/contact`,
    about: { "@id": `${CANONICAL_ORIGIN}/leadership#adeshi-moffi` },
    mainEntity: { "@id": `${CANONICAL_ORIGIN}/#organization` },
  };

  return (
    <>
      <Seo
        title="Contact — Office of the President | Adeshi Moffi, Investors Pathway"
        description="Contact the Office of the President at Investors Pathway LLC. Confidential inquiries from founders, executives, family offices, and institutional investors."
        path="/leadership/contact"
        image={executive.headshot}
        jsonLd={jsonLd}
      />

      <section className="pt-28 md:pt-36 pb-16">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Leadership", to: "/leadership" }, { label: "Contact" }]} />
          <Reveal>
            <div className="mt-10 section-num">07 / Office of the President</div>
            <h1 data-testid="contact-title" className="h-display text-[3rem] md:text-[4.6rem] text-[#0A1128] mt-6 max-w-4xl">
              A confidential conversation.
            </h1>
            <p className="mt-6 body-lead max-w-3xl">
              For advisory mandates, media, or speaking, please provide a brief
              overview. A member of the office will respond directly.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <Reveal>
              <div className="section-num">Direct</div>
              <div className="mt-4">
                <div className="eyebrow">Office</div>
                <address className="mt-2 not-italic text-[#0A1128] font-sans text-sm leading-relaxed">
                  {executive.address.streetAddress}<br />
                  {executive.address.addressLocality}, {executive.address.addressRegion} {executive.address.postalCode}<br />
                  {executive.address.addressCountry}
                </address>
              </div>
              <div className="mt-6">
                <div className="eyebrow">Email</div>
                <a
                  href={`mailto:${executive.contactEmail}`}
                  data-testid="contact-email-link"
                  className="link-underline mt-2 inline-block text-[#0A1128] font-sans text-sm"
                >
                  {executive.contactEmail}
                </a>
              </div>
              <div className="mt-6">
                <div className="eyebrow">Elsewhere</div>
                <ul className="mt-2 space-y-1 text-sm font-sans">
                  {executive.socials.map((s) => (
                    <li key={s.label}>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="me noopener noreferrer"
                        data-testid={`contact-social-${s.label.toLowerCase()}`}
                        className="text-[#0A1128] hover:text-[#8E7331] underline underline-offset-4 decoration-[#E2E8F0] hover:decoration-[#B89947]"
                      >
                        {s.label} <span className="text-[#475569]">— {s.handle}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <div className="col-span-12 md:col-span-8">
            <Reveal>
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="border border-[#E2E8F0] bg-white p-8 md:p-12"
              >
                {submitted ? (
                  <div data-testid="contact-success" className="py-12 text-center">
                    <div className="section-num text-[#8E7331]">Received</div>
                    <h2 className="h-editorial text-3xl md:text-4xl text-[#0A1128] mt-4">
                      Thank you.
                    </h2>
                    <p className="mt-4 body-lead max-w-xl mx-auto">
                      Your message has reached the Office of the President. A member of the
                      team will respond directly and in confidence.
                    </p>
                    <button
                      type="button"
                      onClick={() => setSubmitted(false)}
                      data-testid="contact-send-another"
                      className="btn-outline mt-8"
                    >
                      Send Another
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="eyebrow">Name<span className="text-[#B89947]"> *</span></label>
                        <input
                          type="text"
                          required
                          value={form.name}
                          onChange={onChange("name")}
                          data-testid="contact-input-name"
                          className="mt-2 w-full border-0 border-b border-[#0A1128] bg-transparent py-3 text-[#0A1128] font-sans text-base focus:outline-none focus:border-[#B89947] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="eyebrow">Email<span className="text-[#B89947]"> *</span></label>
                        <input
                          type="email"
                          required
                          value={form.email}
                          onChange={onChange("email")}
                          data-testid="contact-input-email"
                          className="mt-2 w-full border-0 border-b border-[#0A1128] bg-transparent py-3 text-[#0A1128] font-sans text-base focus:outline-none focus:border-[#B89947] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="eyebrow">Company</label>
                        <input
                          type="text"
                          value={form.company}
                          onChange={onChange("company")}
                          data-testid="contact-input-company"
                          className="mt-2 w-full border-0 border-b border-[#0A1128] bg-transparent py-3 text-[#0A1128] font-sans text-base focus:outline-none focus:border-[#B89947] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="eyebrow">Role</label>
                        <input
                          type="text"
                          value={form.role}
                          onChange={onChange("role")}
                          data-testid="contact-input-role"
                          className="mt-2 w-full border-0 border-b border-[#0A1128] bg-transparent py-3 text-[#0A1128] font-sans text-base focus:outline-none focus:border-[#B89947] transition-colors"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="eyebrow">Inquiry Type</label>
                        <select
                          value={form.inquiryType}
                          onChange={onChange("inquiryType")}
                          data-testid="contact-select-inquiry"
                          className="mt-2 w-full border-0 border-b border-[#0A1128] bg-transparent py-3 text-[#0A1128] font-sans text-base focus:outline-none focus:border-[#B89947] transition-colors"
                        >
                          <option>General Inquiry</option>
                          <option>Advisory Mandate</option>
                          <option>Capital Raising</option>
                          <option>Media / Interview</option>
                          <option>Speaking Engagement</option>
                          <option>Partnership</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="eyebrow">Message<span className="text-[#B89947]"> *</span></label>
                        <textarea
                          required
                          rows={5}
                          value={form.message}
                          onChange={onChange("message")}
                          data-testid="contact-input-message"
                          className="mt-2 w-full border border-[#E2E8F0] bg-transparent p-4 text-[#0A1128] font-sans text-base focus:outline-none focus:border-[#0A1128] transition-colors resize-y"
                        />
                      </div>
                    </div>

                    <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
                      <p className="text-xs text-[#475569] font-sans max-w-md leading-relaxed">
                        Submissions are received directly by the Office of the President
                        and handled in confidence.
                      </p>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="contact-submit"
                        className="btn-ink"
                      >
                        {submitting ? "Sending…" : "Send Message"}
                      </button>
                    </div>
                  </>
                )}
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
